import React, { useState } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Button,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Grid,
  Paper,
  Divider,
  Tabs,
  Tab,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Container,
  Stack,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  PlayArrow as PlayArrowIcon,
  Description as DescriptionIcon,
  VideoLibrary as VideoLibraryIcon,
  QuestionAnswer as QuestionAnswerIcon,
  School as SchoolIcon,
  Build as BuildIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  Analytics as AnalyticsIcon,
  Api as IntegrationIcon,
  Help as HelpIcon,
  ContactSupport as ContactSupportIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Chat as ChatIcon,
  GetApp as GetAppIcon,
  Code as CodeIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import HeaderBar from '../components/HeaderBar';

function HelpSupportPage({ darkMode, onToggleTheme }) {
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState(0);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [supportForm, setSupportForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    priority: 'medium',
  });

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleContactSubmit = () => {
    // Handle support form submission
    console.log('Support form submitted:', supportForm);
    setContactDialogOpen(false);
    // Reset form
    setSupportForm({
      name: '',
      email: '',
      subject: '',
      message: '',
      priority: 'medium',
    });
  };

  const agenticFlowFeatures = [
    {
      icon: <BuildIcon />,
      title: 'Visual Flow Designer',
      description: 'Drag-and-drop interface with zoom controls and collapsible panels for creating complex AI workflows',
    },
    {
      icon: <AutoAwesomeIcon />,
      title: 'Multi-Agent Orchestration',
      description: 'LangGraph integration for sophisticated multi-agent workflows and complex AI pipelines',
    },
    {
      icon: <SpeedIcon />,
      title: 'Real-time Chat Interface',
      description: 'Multi-modal chat with text, image, voice input and animated response rendering with pause/resume',
    },
    {
      icon: <IntegrationIcon />,
      title: 'Multi-Model Support',
      description: 'LLaMA 4 API with vision capabilities, Ollama local models (llama3, mistral), and custom endpoints',
    },
    {
      icon: <AnalyticsIcon />,
      title: 'Dynamic Form Builder',
      description: 'JSONForms integration with grid layouts, Material-UI components, and runtime form execution',
    },
    {
      icon: <SecurityIcon />,
      title: 'Enterprise Security',
      description: 'Five-tier RBAC system, JWT/OAuth authentication, password reset, and user management dashboard',
    },
  ];

  const tutorialSteps = [
    {
      title: 'Getting Started with Chat',
      description: 'Learn to use multi-modal chat with LLaMA 4, voice input, and conversation management',
      duration: '10 min',
      difficulty: 'Beginner',
    },
    {
      title: 'Building Dynamic Forms',
      description: 'Create interactive forms with JSONForms, Material-UI components, and validation',
      duration: '25 min',
      difficulty: 'Intermediate',
    },
    {
      title: 'Creating Agentic Workflows',
      description: 'Design multi-agent systems with LangGraph integration and RAG capabilities',
      duration: '40 min',
      difficulty: 'Advanced',
    },
    {
      title: 'User Management & Security',
      description: 'Implement role-based access control, user management, and security features',
      duration: '30 min',
      difficulty: 'Advanced',
    },
    {
      title: 'RAG Document Processing',
      description: 'Set up document retrieval systems with ChromaDB and vector embeddings',
      duration: '45 min',
      difficulty: 'Expert',
    },
    {
      title: 'API Integration & Deployment',
      description: 'Connect external APIs, deploy to production, and monitor performance',
      duration: '60 min',
      difficulty: 'Expert',
    },
  ];

  const faqItems = [
    {
      question: 'What types of AI models are supported?',
      answer: 'The platform supports LLaMA 4 API with vision capabilities, Ollama local models (llama3, mistral), and custom model endpoints. You can switch between models for different tasks and use cases.',
    },
    {
      question: 'How does the role-based access control work?',
      answer: 'The platform implements a five-tier RBAC system:\n• Viewer: Read-only access to dashboard and help\n• User: Basic access to forms and knowledge base\n• Manager: Access to analytics and management features\n• Admin: Full system access except user management\n• Super Admin: Complete access including user administration',
    },
    {
      question: 'Can I build forms without coding?',
      answer: 'Yes! The visual form builder uses a drag-and-drop interface with Material-UI components. It supports JSONForms integration, grid layouts, nested field structures, validation, and real-time form rendering.',
    },
    {
      question: 'What is RAG and how do I use it?',
      answer: 'RAG (Retrieval Augmented Generation) allows you to chat with your documents. Upload PDFs, text files, or other documents to ChromaDB vector storage, and the AI can retrieve relevant information to answer questions about your content.',
    },
    {
      question: 'How do I reset my password?',
      answer: "Click 'Forgot Password' on the signin page, enter your email, and you'll receive a secure reset link. The system uses professional HTML email templates with 1-hour expiring tokens for security.",
    },
    {
      question: 'Can I use voice input and output?',
      answer: 'Yes! The platform supports speech-to-text input and text-to-speech output using the Web Speech API. This enables hands-free interaction with the AI chat system.',
    },
    {
      question: 'How do I create multi-agent workflows?',
      answer: "Use the Agentic Flow Builder with LangGraph integration. Drag nodes onto the canvas, connect them with links, configure each agent's role, and test the workflow. You can create complex pipelines with multiple AI agents working together.",
    },
    {
      question: 'Is the platform mobile-friendly?',
      answer: 'Absolutely! The platform is designed mobile-first as a Progressive Web App (PWA) with touch gestures, responsive layouts, and proper handling of mobile device features like iPhone notches and Android navigation bars.',
    },
    {
      question: 'What is the App Builder?',
      answer: 'The App Builder lets you create custom applications that combine AI chat with data panels (tables, charts, forms). Each app connects to your agentic flows and displays their outputs. Use templates like Lead Qualifier or FAQ Bot to get started quickly.',
    },
    {
      question: 'How do I publish or embed my app?',
      answer: 'In App Builder edit mode, open Settings and find "Publish & embed". Copy the public URL (/p/your-app-id) or the iframe snippet to share or embed your app on any website.',
    },
  ];

  function TabPanel({
    children, value, index, ...other
  }) {
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`help-tabpanel-${index}`}
        aria-labelledby={`help-tab-${index}`}
        {...other}
      >
        {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
      </div>
    );
  }

  return (
    <Box sx={{
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}
    >
      <HeaderBar
        title="Help & Support"
        darkMode={darkMode}
        onToggleTheme={onToggleTheme}
        showBackButton
      />

      <Box
        component="main"
        sx={{
          flex: 1,
          overflow: 'auto',
          bgcolor: 'background.default',
        }}
      >
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
          {/* Hero Section */}
          <Paper
            elevation={3}
            sx={{
              p: { xs: 3, sm: 4 },
              mb: 4,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              borderRadius: 2,
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h3"
              component="h1"
              gutterBottom
              fontWeight="bold"
              sx={{ fontSize: { xs: '2rem', sm: '3rem' } }}
            >
              AI Chat Assistant Platform
            </Typography>
            <Typography
              variant="h6"
              sx={{
                opacity: 0.9,
                mb: 3,
                fontSize: { xs: '1rem', sm: '1.25rem' },
                maxWidth: '800px',
                mx: 'auto',
              }}
            >
              Comprehensive AI chatbot platform with multi-modal capabilities, form building, agentic workflows, and enterprise-grade features
            </Typography>
            <Button
              variant="contained"
              size="large"
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                px: 4,
                py: 1.5,
              }}
              startIcon={<PlayArrowIcon />}
              onClick={() => setSelectedTab(1)}
            >
              Start Learning
            </Button>
          </Paper>

          {/* Navigation Tabs */}
          <Paper elevation={1} sx={{ mb: 3 }}>
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                '& .MuiTab-root': {
                  minHeight: 64,
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                },
              }}
            >
              <Tab label="Overview" icon={<HelpIcon />} iconPosition="start" />
              <Tab label="Tutorials" icon={<SchoolIcon />} iconPosition="start" />
              <Tab label="Documentation" icon={<DescriptionIcon />} iconPosition="start" />
              <Tab label="FAQ" icon={<QuestionAnswerIcon />} iconPosition="start" />
              <Tab label="Contact" icon={<ContactSupportIcon />} iconPosition="start" />
            </Tabs>
          </Paper>

          {/* Tab Content */}
          <TabPanel value={selectedTab} index={0}>
            <Stack spacing={4}>
              <Box>
                <Typography variant="h4" gutterBottom color="primary">
                  Platform Overview
                </Typography>
                <Typography variant="body1" paragraph color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                  A comprehensive full-stack AI chatbot platform with multi-modal capabilities, form building,
                  agentic workflows, conversation management, and enterprise-grade features. Built with React 18,
                  Node.js, MongoDB, and cutting-edge AI technologies.
                </Typography>
              </Box>

              {/* Core Features Grid */}
              <Grid container spacing={3}>
                {[
                  {
                    icon: <ChatIcon />,
                    title: 'Multi-Modal Chat System',
                    description: 'LLaMA 4 API, Ollama local models (llama3, mistral), text/image/file upload support with vision capabilities, voice integration with speech-to-text and text-to-speech',
                  },
                  {
                    icon: <SecurityIcon />,
                    title: 'Authentication & Security',
                    description: 'Multi-factor JWT tokens and Google OAuth 2.0, five-tier RBAC (Viewer, User, Manager, Admin, Super Admin), password reset system, advanced user management',
                  },
                  {
                    icon: <AutoAwesomeIcon />,
                    title: 'Advanced AI Features',
                    description: 'RAG (Retrieval Augmented Generation) with ChromaDB, LangGraph integration for multi-agent workflows, memory system with vector embeddings for semantic search',
                  },
                  {
                    icon: <BuildIcon />,
                    title: 'Form Builder & Dynamic UI',
                    description: 'Visual drag-and-drop interface with Material-UI components, JSONForms integration, grid/table layouts, form management with analytics dashboard',
                  },
                  {
                    icon: <AnalyticsIcon />,
                    title: 'Enterprise Features',
                    description: 'Backend-synchronized user settings, dark/light theme, mobile-first PWA design, real-time notifications, export/import capabilities',
                  },
                  {
                    icon: <IntegrationIcon />,
                    title: 'Technology Stack',
                    description: 'React 18 with Vite, Node.js with Express, MongoDB with Mongoose, Passport.js, JWT authentication, ChromaDB vector storage, LangGraph workflows',
                  },
                ].map((feature, index) => (
                  <Grid item xs={12} md={6} key={index}>
                    <Card
                      elevation={2}
                      sx={{
                        height: '100%',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 4,
                        },
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Box sx={{ color: 'primary.main', mr: 2, fontSize: '2rem' }}>
                            {feature.icon}
                          </Box>
                          <Typography variant="h6" component="h3" fontWeight="bold">
                            {feature.title}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                          {feature.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* Quick Start Guide */}
              <Card elevation={3}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h5" gutterBottom color="primary" fontWeight="bold">
                    Quick Start Guide
                  </Typography>
                  <List sx={{ '& .MuiListItem-root': { py: 2 } }}>
                    {[
                      { title: 'Sign Up & Login', desc: 'Create account with Google OAuth or email/password, set up user profile and role permissions' },
                      { title: 'Explore the Dashboard', desc: 'Navigate the comprehensive dashboard with statistics, quick actions, and recent activity' },
                      { title: 'Start a Chat Session', desc: 'Use multi-modal chat with text, images, voice input and various AI models (LLaMA 4, Ollama)' },
                      { title: 'Build Your First Form', desc: 'Use the visual form builder with drag-drop components, grid layouts, and validation' },
                      { title: 'Create Agentic Workflows', desc: 'Design multi-agent flows with LangGraph integration, RAG capabilities, and custom nodes' },
                      { title: 'Manage & Deploy', desc: 'Test workflows, manage permissions, export/import data, and monitor performance analytics' },
                    ].map((step, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <Box sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            bgcolor: 'primary.main',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1rem',
                            fontWeight: 'bold',
                          }}
                          >
                            {index + 1}
                          </Box>
                        </ListItemIcon>
                        <ListItemText
                          primary={(
                            <Typography variant="h6" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                              {step.title}
                            </Typography>
                          )}
                          secondary={(
                            <Typography variant="body2" color="text.secondary">
                              {step.desc}
                            </Typography>
                          )}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Stack>
          </TabPanel>

          <TabPanel value={selectedTab} index={1}>
            <Stack spacing={4}>
              <Box>
                <Typography variant="h4" gutterBottom color="primary">
                  Step-by-Step Tutorials
                </Typography>
                <Typography variant="body1" paragraph color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                  Learn to build powerful AI workflows with our comprehensive tutorials.
                  Start with basics and progress to advanced multi-agent systems and enterprise features.
                </Typography>
              </Box>

              <Grid container spacing={3}>
                {tutorialSteps.map((tutorial, index) => (
                  <Grid item xs={12} md={6} key={index}>
                    <Card
                      elevation={2}
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 4,
                        },
                      }}
                    >
                      <CardContent sx={{ flexGrow: 1, p: 3 }}>
                        <Box sx={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2,
                        }}
                        >
                          <Typography variant="h6" component="h3" fontWeight="bold">
                            {tutorial.title}
                          </Typography>
                          <Chip
                            label={tutorial.difficulty}
                            size="small"
                            color={
                              tutorial.difficulty === 'Beginner' ? 'success'
                                : tutorial.difficulty === 'Intermediate' ? 'warning'
                                  : tutorial.difficulty === 'Advanced' ? 'error' : 'secondary'
                            }
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary" paragraph sx={{ lineHeight: 1.6 }}>
                          {tutorial.description}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'medium' }}>
                          Duration:
                          {' '}
                          {tutorial.duration}
                        </Typography>
                      </CardContent>
                      <Box sx={{ p: 3, pt: 0 }}>
                        <Button
                          variant="outlined"
                          fullWidth
                          startIcon={<PlayArrowIcon />}
                          sx={{ py: 1.5 }}
                        >
                          Start Tutorial
                        </Button>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* Additional Resources */}
              <Box>
                <Typography variant="h5" gutterBottom color="primary" fontWeight="bold">
                  Additional Learning Resources
                </Typography>
                <Grid container spacing={3}>
                  {[
                    { icon: <VideoLibraryIcon />, title: 'Video Library', desc: 'Watch comprehensive video tutorials' },
                    { icon: <CodeIcon />, title: 'Code Examples', desc: 'Sample flows and code snippets' },
                    { icon: <GetAppIcon />, title: 'Templates', desc: 'Pre-built flow templates' },
                    { icon: <SchoolIcon />, title: 'Webinars', desc: 'Live training sessions' },
                  ].map((resource, index) => (
                    <Grid item xs={12} sm={6} md={3} key={index}>
                      <Card
                        elevation={1}
                        sx={{
                          textAlign: 'center',
                          transition: 'transform 0.2s, box-shadow 0.2s',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: 3,
                          },
                        }}
                      >
                        <CardContent sx={{ p: 3 }}>
                          <Box sx={{ color: 'primary.main', mb: 2 }}>
                            {React.cloneElement(resource.icon, { sx: { fontSize: 48 } })}
                          </Box>
                          <Typography variant="h6" gutterBottom fontWeight="bold">
                            {resource.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {resource.desc}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Stack>
          </TabPanel>

          {/* Documentation Tab */}
          <TabPanel value={selectedTab} index={2}>
            <Stack spacing={4}>
              <Box>
                <Typography variant="h4" gutterBottom color="primary">
                  Technical Documentation
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Card elevation={2}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h6" gutterBottom fontWeight="bold">
                        Complete Reference Guide
                      </Typography>
                      {' '}
                      <List>
                        {[
                          { title: 'Architecture Overview', desc: 'Complete guide to the full-stack architecture and technology stack' },
                          { title: 'App Builder Guide', desc: 'Create apps that combine chat with data panels. Panels, layouts, publish, embed, and templates.', path: '/dashboard-builder' },
                          { title: 'API Reference Guide', desc: 'Comprehensive documentation of all backend APIs and endpoints' },
                          { title: 'Form Builder Components', desc: 'Reference for all available form components and JSONForms integration' },
                          { title: 'Security Implementation', desc: 'Role-based access control, authentication, and security best practices' },
                          { title: 'Deployment Guide', desc: 'Step-by-step production deployment with Docker and Nginx' },
                          { title: 'Troubleshooting Guide', desc: 'Common issues, debugging tips, and performance optimization' },
                        ].map((item, index) => (
                          <ListItem
                            key={index}
                            button
                            onClick={() => item.path && navigate(item.path)}
                            sx={{
                              borderRadius: 1,
                              mb: 1,
                              '&:hover': { bgcolor: 'action.hover' },
                            }}
                          >
                            <ListItemIcon>
                              <DescriptionIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText
                              primary={(
                                <Typography variant="body1" fontWeight="medium">
                                  {item.title}
                                </Typography>
                              )}
                              secondary={item.desc}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Stack spacing={3}>
                    <Card elevation={2}>
                      <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom fontWeight="bold">
                          Quick References
                        </Typography>
                        <Stack spacing={2}>
                          <Button
                            variant="outlined"
                            fullWidth
                            startIcon={<BuildIcon />}
                            sx={{ py: 1.5 }}
                            onClick={() => navigate('/dashboard-builder')}
                          >
                            App Builder
                          </Button>
                          <Button
                            variant="outlined"
                            fullWidth
                            startIcon={<GetAppIcon />}
                            sx={{ py: 1.5 }}
                          >
                            Download PDF Guide
                          </Button>
                          <Button
                            variant="outlined"
                            fullWidth
                            startIcon={<CodeIcon />}
                            sx={{ py: 1.5 }}
                          >
                            API Reference
                          </Button>
                          <Button
                            variant="outlined"
                            fullWidth
                            startIcon={<DescriptionIcon />}
                            sx={{ py: 1.5 }}
                          >
                            Cheat Sheet
                          </Button>
                        </Stack>
                      </CardContent>
                    </Card>

                    <Alert severity="info">
                      <Typography variant="body2">
                        <strong>Pro Tip:</strong>
                        {' '}
                        Use Ctrl+F to quickly search through documentation pages.
                      </Typography>
                    </Alert>
                  </Stack>
                </Grid>
              </Grid>
            </Stack>
          </TabPanel>

          {/* FAQ Tab */}
          <TabPanel value={selectedTab} index={3}>
            <Stack spacing={4}>
              <Box>
                <Typography variant="h4" gutterBottom color="primary">
                  Frequently Asked Questions
                </Typography>
                <Typography variant="body1" paragraph color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                  Find answers to common questions about the AI Chat Assistant Platform.
                </Typography>
              </Box>

              <Box>
                {faqItems.map((faq, index) => (
                  <Accordion key={index} elevation={1} sx={{ mb: 2 }}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{ py: 2 }}
                    >
                      <Typography variant="h6" fontWeight="medium">{faq.question}</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                        {faq.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>

              <Card elevation={2} sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Can't find what you're looking for?
                </Typography>
                <Typography variant="body1" paragraph color="text.secondary">
                  Our support team is here to help with any questions not covered in the FAQ.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<ContactSupportIcon />}
                  onClick={() => setContactDialogOpen(true)}
                  sx={{ px: 4, py: 1.5 }}
                >
                  Contact Support
                </Button>
              </Card>
            </Stack>
          </TabPanel>

          {/* Contact Tab */}
          <TabPanel value={selectedTab} index={4}>
            <Stack spacing={4}>
              <Box>
                <Typography variant="h4" gutterBottom color="primary">
                  Contact Support
                </Typography>
              </Box>

              <Grid container spacing={4}>
                <Grid item xs={12} md={8}>
                  <Card elevation={2}>
                    <CardContent sx={{ p: 4 }}>
                      <Typography variant="h6" gutterBottom fontWeight="bold">
                        Get Help From Our Team
                      </Typography>
                      <Typography variant="body1" paragraph color="text.secondary" sx={{ mb: 3 }}>
                        Choose the best way to reach us based on your needs and urgency.
                      </Typography>

                      <Grid container spacing={3}>
                        {[
                          {
                            icon: <ChatIcon />, title: 'Live Chat', desc: 'Instant help during business hours', action: 'Start Chat',
                          },
                          {
                            icon: <EmailIcon />, title: 'Email Support', desc: 'Detailed help within 24 hours', action: 'Send Email',
                          },
                          {
                            icon: <PhoneIcon />, title: 'Phone Support', desc: 'Priority support for urgent issues', action: 'Call Now',
                          },
                        ].map((contact, index) => (
                          <Grid item xs={12} sm={4} key={index}>
                            <Card
                              variant="outlined"
                              sx={{
                                textAlign: 'center',
                                p: 3,
                                height: '100%',
                                transition: 'transform 0.2s, box-shadow 0.2s',
                                '&:hover': {
                                  transform: 'translateY(-2px)',
                                  boxShadow: 3,
                                },
                              }}
                            >
                              <Box sx={{ color: 'primary.main', mb: 2 }}>
                                {React.cloneElement(contact.icon, { sx: { fontSize: 48 } })}
                              </Box>
                              <Typography variant="h6" gutterBottom fontWeight="bold">
                                {contact.title}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" paragraph>
                                {contact.desc}
                              </Typography>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={index === 1 ? () => setContactDialogOpen(true) : undefined}
                                sx={{ mt: 1 }}
                              >
                                {contact.action}
                              </Button>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Stack spacing={3}>
                    <Card elevation={2}>
                      <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom fontWeight="bold">
                          Support Hours
                        </Typography>
                        <Typography variant="body2" paragraph>
                          <strong>Live Chat & Phone:</strong>
                          <br />
                          Monday - Friday: 9 AM - 6 PM EST
                          <br />
                          Saturday: 10 AM - 4 PM EST
                        </Typography>
                        <Typography variant="body2" paragraph>
                          <strong>Email Support:</strong>
                          <br />
                          24/7 - We respond within 24 hours
                        </Typography>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="h6" gutterBottom fontWeight="bold">
                          Emergency Support
                        </Typography>
                        <Typography variant="body2">
                          For critical production issues, use our emergency hotline available 24/7.
                        </Typography>
                      </CardContent>
                    </Card>
                  </Stack>
                </Grid>
              </Grid>
            </Stack>
          </TabPanel>
        </Container>
      </Box>

      {/* Contact Support Dialog */}
      <Dialog
        open={contactDialogOpen}
        onClose={() => setContactDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Contact Support</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Name"
                fullWidth
                value={supportForm.name}
                onChange={(e) => setSupportForm({ ...supportForm, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                type="email"
                fullWidth
                value={supportForm.email}
                onChange={(e) => setSupportForm({ ...supportForm, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Subject"
                fullWidth
                value={supportForm.subject}
                onChange={(e) => setSupportForm({ ...supportForm, subject: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Message"
                multiline
                rows={4}
                fullWidth
                value={supportForm.message}
                onChange={(e) => setSupportForm({ ...supportForm, message: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleContactSubmit} variant="contained">
            Send Message
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default HelpSupportPage;
